import React, { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  Search,
  RefreshCw,
  X,
  Phone,
  Radio,
  CheckCircle2,
  Sparkles,
  Music2,
} from "lucide-react";
import { API_ENDPOINTS } from "../lib/config";
import { fetchWithAuth } from "../lib/authService";

export default function LiveAudienceModal({
  open,
  onClose,
  venueId = null,
  initialUsers = [],
}) {
  const [users, setUsers] = useState(initialUsers || []);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);

  const fetchLiveUsers = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.VENUE_USERS(venueId));
      if (!res.ok) throw new Error(`Failed to load audience: ${res.status}`);
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Fetch live users error:", err);
      setError(err.message || "Could not fetch venue audience");
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => {
    if (open) {
      if (initialUsers && initialUsers.length > 0) {
        setUsers(initialUsers);
      }
      if (venueId) {
        fetchLiveUsers();
      }
    }
  }, [open, venueId, initialUsers, fetchLiveUsers]);

  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return users;
    const q = searchTerm.toLowerCase();
    return users.filter(
      (u) =>
        (u.display_name && u.display_name.toLowerCase().includes(q)) ||
        (u.first_name && u.first_name.toLowerCase().includes(q)) ||
        (u.phone_number && u.phone_number.includes(q))
    );
  }, [users, searchTerm]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", duration: 0.4 }}
          className="relative w-full max-w-lg max-h-[90vh] overflow-hidden rounded-3xl bg-white shadow-2xl border border-purple-100 flex flex-col"
        >
          {/* Top Header */}
          <div className="p-5 sm:p-6 border-b border-purple-100/80 bg-gradient-to-r from-purple-50 via-white to-emerald-50/50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-tr from-emerald-500 to-emerald-400 flex items-center justify-center text-white shadow-md shadow-emerald-500/20 relative">
                <Users className="w-6 h-6" />
                <span className="absolute -top-1 -right-1 flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                </span>
              </div>
              <div>
                <h2 className="text-xl font-extrabold text-gray-900 flex items-center gap-2">
                  <span>Live Audience</span>
                  <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                    {users.length} Checked In
                  </span>
                </h2>
                <p className="text-xs text-gray-500 font-medium">
                  Attendees currently connected to your live session
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {venueId && (
                <button
                  type="button"
                  onClick={fetchLiveUsers}
                  title="Refresh Crowd"
                  className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl transition-colors"
                >
                  <RefreshCw
                    className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                  />
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="p-4 border-b border-gray-100 bg-gray-50/70">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search attendee by name or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-white border border-purple-100 rounded-xl text-xs font-medium text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#72288D] focus:border-transparent transition-all shadow-2xs"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Attendees List */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-2.5">
            {loading ? (
              <div className="py-16 flex flex-col items-center justify-center space-y-3">
                <div className="w-8 h-8 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                <p className="text-xs font-medium text-gray-500">
                  Scanning checked-in audience...
                </p>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="rounded-3xl p-10 text-center bg-gray-50/70 border border-dashed border-gray-200">
                <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-3">
                  <Users className="w-7 h-7" />
                </div>
                <h4 className="text-base font-bold text-gray-800">
                  {searchTerm ? "No Attendees Found" : "No Audience Checked In Yet"}
                </h4>
                <p className="text-xs text-gray-500 max-w-xs mx-auto mt-1">
                  {searchTerm
                    ? `No audience members matching "${searchTerm}".`
                    : "As fans scan your QR code and request songs, they will appear in your live crowd roster."}
                </p>
              </div>
            ) : (
              filteredUsers.map((user, idx) => {
                const displayName =
                  user.display_name ||
                  (user.first_name
                    ? `${user.first_name} ${user.last_name || ""}`.trim()
                    : "Fan Guest");
                const avatar =
                  user.avatar ||
                  `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    displayName
                  )}&background=72288D&color=fff`;

                return (
                  <motion.div
                    key={user.id || user._id || idx}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.03 }}
                    className="p-3.5 rounded-2xl bg-white border border-purple-50/80 hover:border-purple-200 shadow-2xs hover:shadow-sm transition-all flex items-center justify-between gap-3"
                  >
                    {/* Avatar and name */}
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="relative shrink-0">
                        <img
                          src={avatar}
                          alt={displayName}
                          onError={(e) => {
                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                              displayName
                            )}&background=72288D&color=fff`;
                          }}
                          className="w-11 h-11 rounded-full object-cover border-2 border-purple-100 shadow-xs"
                        />
                        <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-500 ring-2 ring-white"></span>
                      </div>

                      <div className="min-w-0">
                        <p className="text-sm font-bold text-gray-900 truncate">
                          {displayName}
                        </p>
                        <div className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium mt-0.5">
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                          <span>Active in Venue</span>
                        </div>
                      </div>
                    </div>

                    {/* Right side phone or tag */}
                    {user.phone_number && (
                      <a
                        href={`tel:${user.phone_number}`}
                        className="px-2.5 py-1.5 rounded-xl bg-purple-50 text-[#72288D] hover:bg-purple-100 text-xs font-semibold transition-colors shrink-0 flex items-center gap-1"
                        title="Call attendee"
                      >
                        <Phone className="w-3 h-3" />
                        <span className="hidden sm:inline">Call</span>
                      </a>
                    )}
                  </motion.div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between text-xs text-gray-500">
            <span className="flex items-center gap-1.5">
              <Radio className="w-3.5 h-3.5 text-emerald-500 animate-pulse" />
              <span>Real-time audience roster from venue check-ins</span>
            </span>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-1.5 rounded-xl bg-gray-200 text-gray-700 hover:bg-gray-300 font-semibold transition-colors"
            >
              Done
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
}
